import {stacktrace} from '@github-ui/error-serialization'

const MAX_ERRORS = 10
const loadTime = Date.now()

export type ServiceWorkerErrorContext = {
  message?: string
  critical?: boolean
  requestId?: string
  statusCode?: number
  targetUrl?: string
  referrer?: string
  navType?: 'hard' | 'turbo'
}

export type ServiceWorkerFailbotConfig = {
  errorsUrl: string
  user?: string
  catalogService?: string
  release?: string
  actorId?: string
}

let failbotConfig: ServiceWorkerFailbotConfig | undefined
let errorsReported = 0

export function initServiceWorkerFailbot(config: ServiceWorkerFailbotConfig) {
  if (failbotConfig) {
    return
  }
  if (!config.errorsUrl) {
    return
  }

  failbotConfig = config
}

export const resetState = () => {
  failbotConfig = undefined
  errorsReported = 0
}

function isError(error: unknown): error is Error {
  if (error instanceof Error) return true
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof error.name === 'string' &&
    'message' in error &&
    typeof error.message === 'string'
  )
}

function serializeNonError(error: unknown): string {
  try {
    return JSON.stringify(error)
  } catch {
    return 'Unserializable'
  }
}

function isIgnoredNonError(error: unknown): boolean {
  if (!error || typeof error === 'boolean' || typeof error === 'number') {
    return true
  }
  return false
}

function isNetworkError(error: unknown): boolean {
  // Network errors are typically TypeErrors with "Failed to fetch" message
  // These are out of our control (user's network, DNS issues, etc.) and shouldn't be reported
  if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
    return true
  }
  return false
}

function reportable(): boolean {
  return errorsReported < MAX_ERRORS
}

function formatError(error: Error): PlatformJavascriptError {
  return {
    type: error.name,
    value: error.message,
    stacktrace: stacktrace(error),
    catalogService: failbotConfig?.catalogService,
  }
}

function buildErrorContext(
  error: PlatformJavascriptError,
  additionalContext?: ServiceWorkerErrorContext,
): PlatformReportBrowserErrorInput {
  return {
    error,
    sanitizedUrl: additionalContext?.targetUrl || self.location.href,
    readyState: 'complete',
    referrer: additionalContext?.referrer,
    timeSinceLoad: Math.round(Date.now() - loadTime),
    user: failbotConfig?.user,
    actorId: failbotConfig?.actorId,
    bundler: 'service-worker',
    ui: false,
    release: failbotConfig?.release,
    turbo: additionalContext?.navType === 'turbo',
    pastRequestIds: additionalContext?.requestId ? [additionalContext.requestId] : undefined,
  }
}

function report(context: PlatformReportBrowserErrorInput): void {
  if (!reportable()) return
  if (!failbotConfig?.errorsUrl) return

  errorsReported++

  try {
    fetch(failbotConfig.errorsUrl, {
      method: 'POST',
      body: JSON.stringify({context, target: 'full'}),
    })
  } catch {
    // Error reporting failed - silently ignore
  }
}

export function reportError(error: unknown, context?: ServiceWorkerErrorContext): void {
  if (!failbotConfig) {
    return
  }

  // Don't report network errors - they are out of our control
  if (isNetworkError(error)) {
    return
  }

  if (!isError(error)) {
    if (isIgnoredNonError(error)) return

    const errorForStackTrace = new Error()
    const serializedErrorValue = serializeNonError(error)
    const newError: PlatformJavascriptError = {
      type: 'UnknownError',
      value: `Unable to report error, due to a thrown non-Error type: ${typeof error}, with value ${serializedErrorValue}`,
      stacktrace: stacktrace(errorForStackTrace),
      catalogService: failbotConfig?.catalogService,
    }
    report(buildErrorContext(newError, context))
    return
  }

  report(buildErrorContext(formatError(error), context))
}

// Export for testing
export const getErrorsReported = () => errorsReported
